import { useMemo, useState } from "react";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useAppSelector } from "../../../app/hooks";
import { isHigherThanDistrictAdmin } from "../../../app/userSlice";
import ModalFrame from "../../../components/ModalFrame";
import Tabs, { SubTab } from "../../../components/Tabs";
import SchoolInfoSection from "../../schools/SchoolInfoSection";
import { emptySchoolViewModel } from "../../schools/schoolsApiSlice";
import AddExistingSchoolToUser from "../../schools/AddExistingSchoolToUserForm";

export enum AddSchoolSubTabs {
	Existing = "Existing School",
	New = "New School",
}

interface AddSchoolProps {
	userId: number;
	schoolIds: number[];
}

export default function AddSchoolToUserButton(props: AddSchoolProps) {
	const { role } = useAppSelector((state) => state.user);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [currentTabName, setCurrentTabName] = useState(AddSchoolSubTabs.Existing);

	const initialSubTabs: SubTab[] = useMemo(
		() => [
			{
				name: AddSchoolSubTabs.Existing,
				current: currentTabName === AddSchoolSubTabs.Existing,
				content: (
					<AddExistingSchoolToUser
						userId={props.userId}
						schoolIds={props.schoolIds}
						onClose={handleCloseForm}
					/>
				),
			},
			{
				name: AddSchoolSubTabs.New,
				current: currentTabName === AddSchoolSubTabs.New,
				content: (
					<SchoolInfoSection
						schoolInfo={emptySchoolViewModel}
						addingNew={true}
						userIdToAdd={props.userId}
						saveButtonText="Add School"
						setIsDialogOpen={setIsFormOpen}
					/>
				),
			},
		],
		[props, currentTabName]
	);

	function handleOpenForm() {
		setIsFormOpen(true);
		setCurrentTabName(AddSchoolSubTabs.Existing);
	}

	function handleCloseForm() {
		setIsFormOpen(false);
	}

	return (
		<>
			<Button id="add-school-button" onClick={handleOpenForm} className="btn btn-primary">
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add
				</span>
			</Button>

			<ModalFrame
				open={isFormOpen}
				title="Add User To School"
				onClose={handleCloseForm}
				maxWidth="max-w-[750px]"
				overflow="overflow-visible">
				{role && isHigherThanDistrictAdmin(role) ? (
					<Tabs
						initialSubTabs={initialSubTabs}
						ariaLabel="Add User To School Tabs"
						containerClass="mt-1.5"
						tabPadding="p-2"
					/>
				) : (
					<AddExistingSchoolToUser
						userId={props.userId}
						schoolIds={props.schoolIds}
						onClose={handleCloseForm}
					/>
				)}
			</ModalFrame>
		</>
	);
}
